// Industry Insights Service
// This service handles fetching and caching latest industry insights and sourcing trends

class IndustryInsightsService {
  constructor() {
    this.cacheKey = 'industryInsights_v4';
    this.lastUpdateKey = 'industryInsightsLastUpdate_v4';
    this.cacheTimeout = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  }

  // Get the latest industry insights
  async getLatestInsights() {
    try {
      // Check if we have cached data that's still fresh
      const cachedData = this.getCachedInsights();
      if (cachedData && this.isCacheValid()) {
        return cachedData;
      }

      // If no cache or cache is expired, fetch new data
      const freshInsights = await this.fetchFreshInsights();
      this.cacheInsights(freshInsights);
      return freshInsights;
    } catch (error) {
      console.error('Error fetching industry insights:', error);
      // Return fallback insights if fetch fails
      return this.getFallbackInsights();
    }
  }

  // Check if cached data is still valid (less than 30 days old)
  isCacheValid() {
    const lastUpdate = localStorage.getItem(this.lastUpdateKey);
    if (!lastUpdate) return false;
    
    const lastUpdateTime = parseInt(lastUpdate);
    const now = Date.now();
    return (now - lastUpdateTime) < this.cacheTimeout;
  }

  // Get cached insights from localStorage
  getCachedInsights() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error reading cached insights:', error);
      return null;
    }
  }

  // Cache insights to localStorage
  cacheInsights(insights) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(insights));
      localStorage.setItem(this.lastUpdateKey, Date.now().toString());
    } catch (error) {
      console.error('Error caching insights:', error);
    }
  }

  // Fetch fresh industry insights (in real implementation, this would call an API or web scraping service)
  async fetchFreshInsights() {
    const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const currentYear = new Date().getFullYear();
    
    // Rotating insights pool - different articles appear on each refresh
    const insightsPool = [
      {
        id: `insight-${Date.now()}-1`,
        title: `Global Sourcing Trends ${currentYear}: AI-Driven Supply Chain Optimization`,
        excerpt: "How artificial intelligence is revolutionizing manufacturing sourcing, quality control, and logistics coordination in China's industrial sectors.",
        image: "https://static.prod-images.emergentagent.com/jobs/1e377d72-35c9-4698-8603-668ab4051cd7/images/17143c0b82aa90cecb4611486ae8411c08ab23f610c339b126f300522e44dcee.jpeg",
        date: this.getCurrentMonthDate(1),
        category: "AI & Technology",
        readTime: "7 min read",
        trending: true,
        url: "https://www.weforum.org/stories/2025/06/how-china-is-reinventing-the-future-of-global-manufacturing/"
      },
      {
        id: `insight-${Date.now()}-2`,
        title: `${currentMonth} Market Analysis: Sustainable Manufacturing in China`,
        excerpt: "Green manufacturing initiatives, eco-friendly materials, and sustainability certifications driving the future of Chinese production.",
        image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80",
        date: this.getCurrentMonthDate(2),
        category: "Sustainability",
        readTime: "5 min read",
        trending: false,
        url: "https://www.china-briefing.com/news/china-manufacturing-industry-tracker-2024-25/"
      },
      {
        id: `insight-${Date.now()}-3`,
        title: "Supply Chain Resilience: Modern Sourcing Strategies",
        excerpt: "Building robust supplier networks, diversification strategies, and risk management in international sourcing operations.",
        image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(3),
        category: "Strategy",
        readTime: "6 min read",
        trending: true,
        url: "https://www.china-briefing.com/news/chinas-imports-2025-trends-implications/"
      },
      {
        id: `insight-${Date.now()}-4`,
        title: `${currentYear} E-commerce Export Boom: China to Global Markets`,
        excerpt: "Cross-border e-commerce platforms facilitate direct manufacturer-to-consumer sales, revolutionizing international trade dynamics.",
        image: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(0),
        category: "E-commerce",
        readTime: "5 min read",
        trending: true,
        url: "https://www.trade.gov/country-commercial-guides/china-ecommerce"
      },
      {
        id: `insight-${Date.now()}-5`,
        title: "Quality Control Evolution: Smart Factory Inspections",
        excerpt: "IoT sensors, computer vision, and real-time monitoring transform quality assurance in Chinese manufacturing facilities.",
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(1),
        category: "Quality Control",
        readTime: "6 min read",
        trending: false,
        url: "https://www.mckinsey.com/capabilities/operations/our-insights/industry-4-0-how-to-navigate-digitization-of-the-manufacturing-sector"
      },
      {
        id: `insight-${Date.now()}-6`,
        title: `${currentMonth}: New Trade Corridors Open Between China and Europe`,
        excerpt: "Enhanced rail and maritime routes reduce shipping times by 40%, creating new opportunities for time-sensitive manufacturing.",
        image: "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(2),
        category: "Logistics",
        readTime: "5 min read",
        trending: true,
        url: "https://www.china-briefing.com/"
      },
      {
        id: `insight-${Date.now()}-7`,
        title: "Intellectual Property Protection: China's Enhanced Framework",
        excerpt: "Strengthened IP laws and enforcement mechanisms provide better protection for international brands and innovators.",
        image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(4),
        category: "Legal & Compliance",
        readTime: "6 min read",
        trending: false,
        url: "http://english.mofcom.gov.cn/"
      },
      {
        id: `insight-${Date.now()}-8`,
        title: `${currentYear} Viral Products Report: What's Trending Worldwide`,
        excerpt: "A curated look at the hottest consumer products going viral online — from smart gadgets to lifestyle accessories — shipping direct from Chinese manufacturers.",
        image: "https://images.unsplash.com/photo-1768987439370-bd60d3d0b28b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHx0cmVuZGluZyUyMGNvbnN1bWVyJTIwcHJvZHVjdHMlMjBvbmxpbmUlMjBzaG9wcGluZ3xlbnwwfHx8fDE3NzcwMTkzNjh8MA&ixlib=rb-4.1.0&q=85",
        date: this.getCurrentMonthDate(1),
        category: "Trending Products",
        readTime: "5 min read",
        trending: true,
        url: "https://www.statista.com/topics/1002/global-e-commerce/"
      },
      {
        id: `insight-${Date.now()}-9`,
        title: "Electronics Manufacturing: China's 5G and IoT Production Surge",
        excerpt: "Next-generation electronics manufacturing facilities deliver cutting-edge 5G devices, IoT sensors, and smart home products.",
        image: "https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(0),
        category: "Electronics",
        readTime: "7 min read",
        trending: true,
        url: "https://www.reuters.com/technology/chinas-tech-industry/"
      }
    ];
    
    // Return 6 random articles from the pool
    const shuffled = insightsPool.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 6);
  }

  // Generate a current month date
  getCurrentMonthDate(daysAgo) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  // Fallback insights in case of fetch failure
  getFallbackInsights() {
    return [
      {
        id: 'fallback-insight-1',
        title: "Strategic Sourcing Best Practices",
        excerpt: "Essential strategies for successful international sourcing and supplier relationship management.",
        image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
        category: "Best Practices",
        readTime: "5 min read",
        trending: false
      },
      {
        id: 'fallback-insight-2',
        title: "Quality Assurance in Manufacturing",
        excerpt: "Implementing effective quality control measures in Chinese manufacturing partnerships.",
        image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2187&q=80",
        date: new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
        category: "Quality Control",
        readTime: "6 min read",
        trending: false
      }
    ];
  }

  // Manually refresh insights (can be called from admin or by user action)
  async forceRefresh() {
    try {
      const freshInsights = await this.fetchFreshInsights();
      this.cacheInsights(freshInsights);
      return freshInsights;
    } catch (error) {
      console.error('Error force refreshing insights:', error);
      throw error;
    }
  }

  // Get time until next auto-update
  getTimeUntilNextUpdate() {
    const lastUpdate = localStorage.getItem(this.lastUpdateKey);
    if (!lastUpdate) return 0;
    
    const lastUpdateTime = parseInt(lastUpdate);
    const nextUpdate = lastUpdateTime + this.cacheTimeout;
    const now = Date.now();
    
    return Math.max(0, nextUpdate - now);
  }

  // Get formatted time until next update
  getFormattedTimeUntilUpdate() {
    const timeLeft = this.getTimeUntilNextUpdate();
    const days = Math.floor(timeLeft / (24 * 60 * 60 * 1000));
    
    if (days === 0) return "Updates available now";
    if (days === 1) return "Next update in 1 day";
    return `Next update in ${days} days`;
  }
}

export default new IndustryInsightsService();